import { runtimeEnv } from '@/config/featureFlags';

/**
 * GA4 单事件参数上限口径
 * 详见 doc/开发标准/analytics_事件参数治理方案_初稿_2026-10-05.md
 *
 *   总参数（= 自有 + 全局 + GA4 自动采集）硬上限 36
 *   GA4 自动采集参数实测在 7~11 项之间浮动，且**运行时不可观测**
 *
 * ⇒ 用上下界分别判定两种不同严重度：
 *   - 必然超限：自有 + GA4_AUTO_PARAM_MIN > 36（自动数取最小都超）
 *   - 超出安全预算：自有 + GA4_AUTO_PARAM_MAX > 33（留 3 个安全垫）
 *
 * ⚠️ 超限时 GA4 会**静默丢弃**参数，且被丢的是哪几个不可控、与追加顺序不单调，
 * 所以必须在代码侧提前告警，而不是等线上发现字段缺失。
 * 注意「自有」= 本链路实际发出的参数（业务参数 + buildAnalyticsPayload 追加的全局字段）。
 */
export const GA4_TOTAL_PARAM_LIMIT = 36;
export const GA4_SAFE_TOTAL_PARAM_LIMIT = 33;
export const GA4_AUTO_PARAM_MIN = 7;
export const GA4_AUTO_PARAM_MAX = 11;

// 同一事件 + 同一参数条数只报一次，避免高频事件刷屏
const reportedSignatures = new Set();

export function resetAnalyticsParamBudgetReport() {
  reportedSignatures.clear();
}

/**
 * 纯函数：按「本链路实际发出的参数条数」判定预算档位。
 * 独立导出便于单测边界（不依赖 runtimeEnv）。
 *
 * @param {number} ownCount 业务参数 + 全局字段的条数
 * @returns {{ ownCount: number, bestCaseTotal: number, worstCaseTotal: number, level: 'ok'|'over-budget'|'overflow' }}
 */
export function resolveAnalyticsParamBudget(ownCount) {
  const normalized = Number.isFinite(ownCount) && ownCount > 0 ? Math.floor(ownCount) : 0;
  const bestCaseTotal = normalized + GA4_AUTO_PARAM_MIN;
  const worstCaseTotal = normalized + GA4_AUTO_PARAM_MAX;

  let level = 'ok';
  if (bestCaseTotal > GA4_TOTAL_PARAM_LIMIT) {
    level = 'overflow';
  } else if (worstCaseTotal > GA4_SAFE_TOTAL_PARAM_LIMIT) {
    level = 'over-budget';
  }

  return { ownCount: normalized, bestCaseTotal, worstCaseTotal, level };
}

function isEventDefinition(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.name === 'string'
  );
}

export function validateAnalyticsEvent(eventDefinitionOrName, payload = {}) {
  if (!runtimeEnv.isDevelopment) {
    return true;
  }

  if (isEventDefinition(eventDefinitionOrName)) {
    const definition = eventDefinitionOrName;

    if (Array.isArray(definition.requiredParams)) {
      for (const param of definition.requiredParams) {
        if (!(param in payload)) {
          console.warn(`[Analytics] Missing required param "${param}" for event "${definition.name}"`);
        }
      }
    }
  }

  return true;
}

/**
 * dev 期参数条数断言：入参应是 buildAnalyticsPayload 产出的**最终 payload**。
 * 只做告警，不阻断上报（上报被丢由 GA4 侧决定，代码侧改不了）。
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} payload 最终 payload（含全局字段）
 * @returns {boolean} 恒真，仅为调用方写法一致
 */
export function assertAnalyticsParamBudget(eventName, payload = {}) {
  if (!runtimeEnv.isDevelopment || !eventName) {
    return true;
  }

  const ownCount = Object.keys(payload).length;
  if (!ownCount) {
    return true;
  }

  const { bestCaseTotal, worstCaseTotal, level } = resolveAnalyticsParamBudget(ownCount);
  if (level === 'ok') {
    return true;
  }

  const signature = `${eventName}#${ownCount}`;
  if (reportedSignatures.has(signature)) {
    return true;
  }
  reportedSignatures.add(signature);

  const detail = `自有+全局 ${ownCount} 个，加 GA4 自动 ${GA4_AUTO_PARAM_MIN}~${GA4_AUTO_PARAM_MAX} 个 ⇒ 总参数 ${bestCaseTotal}~${worstCaseTotal} 个`;

  if (level === 'overflow') {
    console.error(
      `[Analytics] 事件 "${eventName}" 参数必然超限（硬上限 ${GA4_TOTAL_PARAM_LIMIT}）：${detail}。GA4 将静默丢弃部分参数，请精简后再上报。`
    );
  } else {
    console.warn(
      `[Analytics] 事件 "${eventName}" 参数超出安全预算（目标 ≤ ${GA4_SAFE_TOTAL_PARAM_LIMIT}）：${detail}。已接近硬上限，建议精简。`
    );
  }

  return true;
}
